/*eslint max-lines-per-function: ["error", 500]*/
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
// import Select from 'react-select';
import { Button, Card, Col, Row, Tab } from 'react-bootstrap';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { type SlotInfo, type View, Views } from 'react-big-calendar';

import { VisitRegistrationEditor } from '../../components/VisitsRegistrations/VisitRegistrationEditor';
import { VisitRegistrationSearch } from '../../components/VisitsRegistrations/VisitRegistrationSearch';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  BigCalendar,
  EventVisit,
  ResourceVisit,
} from '../../../common/components/Calendar/BigCalendar';
import { CalendarLegend } from '../../../common/components/Calendar/CalendarLegend';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import {
  CreateUpdateVisitRegistrationDto,
  GetExamDto,
  GetExamTypeDto,
  GetPaymentDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  convert,
  newVisit,
  validate,
} from '../../../common/spdCore/contexts/VisitRegistrationContext';
import { useNotifications } from '../../../common/hooks/useNotifications';
import HealthcareFacilitySelector from '../../components/Selects/HealthcareFacilitySelector';
import { useCookies } from '../../../common/hooks/useCookies';
import { TabsHistory } from '../../../app/components/TabsHistory';
import FormRow from '../../../app/components/FormRow';

export const VisitRegistrationsView: React.FC = () => {
  const cookies = useCookies();

  const [modal, setModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [events, setEvents] = useState<EventVisit[]>([]);
  const [resources, setResources] = useState<ResourceVisit[]>([]);
  const [visitRegistration, setVisitRegistration] =
    useState<CreateUpdateVisitRegistrationDto>();
  const [exam, setExam] = useState<GetExamDto>();
  const [examType, setExamType] = useState<GetExamTypeDto>();
  const [payments, setPayments] = useState<GetPaymentDto[]>([]);
  const [creatorName, setCreatorName] = useState<string>();
  const [healthcareFacilityId, setHealthcareFacilityId] = useState<
    string | undefined
  >(cookies.healthcareFacilitySelector);
  const [id, setId] = useState<string>();
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<View>(Views.WEEK);
  const [state, setState] = useState<{
    selectInfo?: any;
    state?: any;
    clickInfo?: any;
    checkInfo?: any;
  }>({});
  const notifications = useNotifications();

  const spd = useSpdCore();

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });

  const handleRangeChange = (range: Date[] | { start: Date; end: Date }) => {
    if (Array.isArray(range)) {
      setDateRange({ start: range[0], end: range[range.length - 1] });
    } else {
      setDateRange(range);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => setRefresh(prev => !prev), 30000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    spd.appointmentChannels
      .getAll(healthcareFacilityId)
      .then(data => {
        const result = data.map(item => {
          return {
            resourceTitle: item.name
              ? item.name + ' (' + item.healthcareFacility?.displayName + ')'
              : '',
            resourceId: item.id,
            healthCareFacilityName: item.healthcareFacility?.displayName || '',
          };
        });
        setResources(result);
      })
      .catch(errorResponse => {
        console.log(errorResponse);
      });
  }, [spd.appointmentChannels, refresh, healthcareFacilityId]);

  useEffect(() => {
    spd.visits
      .getAll(
        healthcareFacilityId,
        dateRange.start || undefined,
        dateRange.end || undefined,
      )
      .then(data => {
        const result = data.map(item => {
          return {
            title: `${item.surname || ''} ${item.firstname || ''} ${item.payments && item.payments.length > 0 ? '💰' : ''}`,
            start: item.visitDateTime,
            end: item.visitDateTime
              ? new Date(
                  item.visitDateTime.getTime() +
                    (item.visitTime || 0) * 60 * 1000,
                )
              : null,
            allDay: !item.visitDateTime,
            id: item.id,
            resourceId: item.appointmentChannel?.id || '',
            finished: item.exam?.id ? true : false,
            external: item.externalBooking ?? false,
          };
        });
        setEvents(result);
      })
      .catch(errorResponse => {
        console.log(errorResponse);
      });
  }, [spd.visits, refresh, healthcareFacilityId, dateRange]);

  const handleCloseModal = () => {
    handleClose();
    setModal(false);
  };

  async function handleEventAdd(slotInfo?: SlotInfo) {
    setVisitRegistration(await newVisit(slotInfo?.start, slotInfo?.end));
    setExam(undefined);
    setCreatorName(undefined);
    setId(undefined);
    setModal(true);
  }

  function handleEventClick(clickInfo: any) {
    spd.visits
      .get(clickInfo.id)
      .then(data => {
        setVisitRegistration(convert(data));
        setExam(data.exam || undefined);
        setExamType(data.examType || undefined);
        setPayments(data.payments || []);
        setCreatorName(data.creatorName || undefined);
        setId(clickInfo.id);
        setState({ clickInfo, state: 'update' });
        setModal(true);
      })
      .catch(errorResponse => {
        console.log(errorResponse);
      });
  }

  function handleEventDrop(checkInfo: any) {
    setState({ checkInfo, state: 'przesunąć tę wizytę' });
    spd.visits
      .get(checkInfo.event.id)
      .then(data => {
        setVisitRegistration(
          convert({ ...data, visitDateTime: checkInfo?.start }),
        );
        setExam(data.exam || undefined);
        setCreatorName(data.creatorName || undefined);
        setId(checkInfo.event.id);
      })
      .catch(errorResponse => {
        console.log(errorResponse);
      });
    setConfirmModal(true);
  }

  function handleEventResize(checkInfo: any) {
    setState({ checkInfo, state: 'zmienić godziny tej wizyty' });
    spd.visits
      .get(checkInfo.event.id)
      .then(data => {
        setVisitRegistration(
          convert({
            ...data,
            visitDateTime: checkInfo?.start,
            visitTime:
              checkInfo?.start && checkInfo?.end
                ? (checkInfo?.end.getTime() - checkInfo?.start.getTime()) /
                  (1000 * 60)
                : null,
          }),
        );
        setExam(data.exam || undefined);
        setCreatorName(data.creatorName || undefined);
        setId(checkInfo.event.id);
      })
      .catch(errorResponse => {
        console.log(errorResponse);
      });
    setConfirmModal(true);
  }

  const handleSubmit = async (
    visitCU: CreateUpdateVisitRegistrationDto,
    id?: string,
  ) => {
    const status = await validate(visitCU);
    if (status.valid) {
      try {
        if (id) {
          await spd.visits.update(id, visitCU);
        } else {
          await spd.visits.create(visitCU);
        }
        setRefresh(!refresh);
        handleClose();
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  async function handleDelete(visitId: string) {
    if (visitId) {
      notifications.onPromise(
        spd.visits.changeToInactive(visitId),
        handleCloseWithRefresh,
      );
    }
  }

  function handleClose() {
    setState({});
    setModal(false);
    setConfirmModal(false);
  }

  function handleCloseWithRefresh() {
    setRefresh(!refresh);
    handleClose();
  }

  const getData = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      spd.visits.getAllByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ),
    [spd.visits],
  );

  const getCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      spd.visits.getAllCount(filterText, filterColumns),
    [spd.visits],
  );

  return (
    <Card className="mb-2">
      <Card.Header>Rejestracja wizyt</Card.Header>
      <Card.Body className="m-3">
        <Row className="mb-3">
          <Col></Col>
          <Col>
            <Button
              variant="outline-secondary"
              style={{ float: 'right' }}
              onClick={() => handleEventAdd()}
            >
              Zarejestruj
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <TabsHistory
              activeKey="calendar"
              id="visit-registration-tabs"
              className="mb-3"
            >
              <Tab eventKey="calendar" title="Kalendarz" className="m-3">
                <FormRow controlId="healthcareFacility" label="Placówka">
                  <HealthcareFacilitySelector
                    value={healthcareFacilityId || ''}
                    onChange={e => {
                      setHealthcareFacilityId(
                        e.value === 'All' ? undefined : e.value,
                      );
                      if (e.value !== 'All') {
                        cookies.setHealthcareFacilitySelector(e.value);
                      }
                    }}
                    placeholder="Wybierz placówkę"
                    className={'selector-healthcare mb-3'}
                    addAll={true}
                  />
                </FormRow>

                <CalendarLegend />

                <BigCalendar
                  events={events}
                  handleEventClick={handleEventClick}
                  handleAddEvent={handleEventAdd}
                  handleEventDrop={handleEventDrop}
                  handleEventResize={handleEventResize}
                  handleRangeChange={handleRangeChange}
                  resources={resources}
                  date={calendarDate}
                  view={calendarView}
                  onNavigate={setCalendarDate}
                  onView={setCalendarView}
                />
              </Tab>
              <Tab eventKey="search" title="Wyszukiwanie" className="m-3">
                <VisitRegistrationSearch
                  getData={getData}
                  getCount={getCount}
                  onRowClick={async (id: string) => {
                    handleEventClick({ id });
                  }}
                  refresh={refresh}
                  withFilterInColumns={true}
                />
              </Tab>
            </TabsHistory>
          </Col>
        </Row>

        <VisitRegistrationEditor
          visitRegistration={visitRegistration}
          creatorName={creatorName}
          id={id}
          title={
            state.state === 'update' ? 'Zmień rezerwację' : 'Dodaj rezerwację'
          }
          show={modal}
          onCancel={handleCloseModal}
          onSubmit={handleSubmit}
          submitText={state.clickInfo ? 'Zaktualizuj' : 'Zapisz'}
          onDelete={state.clickInfo && handleDelete}
          addVisit={true}
          exam={exam}
          examType={examType}
          payments={payments}
        />

        <VisitRegistrationEditor
          visitRegistration={visitRegistration}
          creatorName={creatorName}
          id={id}
          title={
            state.state === 'resize'
              ? 'Zmień czas rejestracji'
              : 'Przesuń rejestrację'
          }
          show={confirmModal}
          onCancel={() => {
            state.checkInfo?.revert?.();
            setConfirmModal(false);
          }}
          onSubmit={handleSubmit}
          submitText={'OK'}
          askQuestion={`Czy chciałbyś ${state.state}?`}
          addVisit={true}
        />
      </Card.Body>
    </Card>
  );
};
